import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, cn } from "@scribenote/brian-design-system";
import {
  useFailedRecordingsStore,
  type FailedRecording,
} from "@transcription/failedRecordingsStore";
import {
  dismissFailedRecording,
  retryFailedRecording,
} from "@transcription/recordingsClient";
import { formatRelativeTimeMs } from "@utils/format-date";
import { useState } from "react";

const FailedUploadCard = ({ recording }: { recording: FailedRecording }) => (
  <div
    data-testid={`failed-upload-${recording.id}`}
    className="flex flex-col gap-3 rounded-paper border border-rule bg-card p-4 shadow-lift"
  >
    <div className="flex items-start gap-3">
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        aria-hidden="true"
        className="mt-0.5 shrink-0 text-ink-flag"
      />
      <span className="min-w-0 flex-1 text-sm text-ink-body">
        {`A recording from ${formatRelativeTimeMs(recording.createdAt)} failed to upload.`}
      </span>
    </div>
    <div className="flex justify-end gap-2">
      <Button
        variant="ghost"
        size="sm"
        className="text-ink-stopped"
        data-testid={`dismiss-failed-upload-${recording.id}`}
        onClick={() => void dismissFailedRecording(recording.id)}
      >
        Discard
      </Button>
      <Button
        size="sm"
        data-testid={`retry-failed-upload-${recording.id}`}
        onClick={() => void retryFailedRecording(recording.id)}
      >
        Retry
      </Button>
    </div>
  </div>
);

export const FailedUploadsBanner = () => {
  const failed = useFailedRecordingsStore((s) => s.failed);
  const [open, setOpen] = useState(false);

  if (failed.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`${failed.length} failed upload${failed.length === 1 ? "" : "s"}`}
        data-testid="failed-uploads-toggle"
        className={cn(
          "relative flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full",
          "text-ink-flag transition-colors duration-150 ease-paper motion-reduce:transition-none",
          "hover:bg-recess",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
        )}
      >
        <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" />
        {failed.length > 1 && (
          <span
            aria-hidden="true"
            className={cn(
              "absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1",
              "border border-rule bg-card font-mono text-[0.625rem] leading-none text-ink shadow-lift",
            )}
          >
            {failed.length}
          </span>
        )}
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        // Dropped while closed: a native <dialog> stays mounted.
        data-testid={open ? "failed-uploads-modal" : undefined}
        title="Failed uploads"
      >
        <div className="mx-auto flex max-w-[38rem] flex-col gap-3">
          {failed.map((recording) => (
            <FailedUploadCard key={recording.id} recording={recording} />
          ))}
        </div>
      </Dialog>
    </>
  );
};
